/**
 * Copyright © 2025 Delicious AI, LLC
 *
 * @author Mitch Wilson <mitchell.wilson@deliciousai.com>
 */

import { useLazyQuery, useMutation } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import { TitledPaper } from '@dai/components'
import { Alert, CircularProgress, Link, Typography } from '@mui/material'
import { useEffect } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'

import CreateAccount from '@/components/auth/CreateAccount'
import CenteredBox from '@/components/CenteredBox'
import {
  GET_INVITATION,
  GetInvitationQuery,
  GetInvitationQueryVariables,
  UPDATE_INVITATION,
  UpdateInvitationMutation,
  UpdateInvitationMutationVariables,
} from '@/graphql'
import useT from '@/i18n/useT'

function AcceptInvite() {
  const { t } = useT()
  const [searchParams] = useSearchParams()
  const {
    handle: { handleNavTo },
  } = useUrlSearchParams()
  const invitationId = searchParams.get('id')
  const [getInvitation, invitation] = useLazyQuery<
    GetInvitationQuery,
    GetInvitationQueryVariables
  >(GET_INVITATION, {
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (invitationId) {
      getInvitation({
        variables: {
          id: invitationId,
        },
      })
    }
  }, [invitationId, getInvitation])

  const [updateInvitation] = useMutation<
    UpdateInvitationMutation,
    UpdateInvitationMutationVariables
  >(UPDATE_INVITATION)

  const invalidateInvite = async () => {
    if (!invitation.data?.invitation.id) return
    await updateInvitation({
      variables: {
        data: {
          id: invitation.data.invitation.id,
          opened: true,
        },
      },
    })
  }

  const invite = invitation.data?.invitation

  const ReturnToLogin = (
    <Typography sx={{ textAlign: 'center' }}>
      {t('acceptInvite.alreadyHaveAccount')}
      <Link to="/auth/sign-in" component={RouterLink}>
        {t('acceptInvite.login')}
      </Link>
    </Typography>
  )

  if (invitation.loading) {
    return (
      <CenteredBox>
        <CircularProgress />
      </CenteredBox>
    )
  }

  if (invitation.error) {
    return (
      <TitledPaper title={t('acceptInvite.invalidInvitation')}>
        <Alert severity="error">{invitation.error.message}</Alert>
        {ReturnToLogin}
      </TitledPaper>
    )
  }

  if (!invite) {
    return (
      <TitledPaper
        title={t('acceptInvite.invalidInvitation')}
        subtitle={t('acceptInvite.invitationNotFound')}
      >
        {ReturnToLogin}
      </TitledPaper>
    )
  }

  if (invite.invalidated) {
    return (
      <TitledPaper
        title={t('acceptInvite.invitationInvalidated')}
        subtitle={t('acceptInvite.invitationInvalidatedMessage')}
      >
        {ReturnToLogin}
      </TitledPaper>
    )
  }

  if (invite.opened) {
    return (
      <TitledPaper
        title={t('acceptInvite.invitationAccepted')}
        subtitle={t('acceptInvite.invitationAlreadyAccepted')}
      >
        {ReturnToLogin}
      </TitledPaper>
    )
  }

  if (!invite.inviter.tenant?.id || !invite.groups.length) {
    return (
      <TitledPaper
        title={t('acceptInvite.invalidInvitation')}
        subtitle={t('acceptInvite.invitationInvalid')}
      >
        {ReturnToLogin}
      </TitledPaper>
    )
  }

  return (
    <TitledPaper
      title={t('acceptInvite.invitationTitle', {
        tenantName: invite.inviter.tenant?.name,
        groupName: invite.groups[0].name,
      })}
      subtitle={t('acceptInvite.invitationSubtitle')}
    >
      <CreateAccount
        invitation={{
          email: invite.email,
          tenantId: invite.inviter.tenant.id,
          groupIds: invite.groups.map(({ id }) => id),
          storeIds: invite.stores.map(({ id }) => id),
        }}
        onSuccess={() => {
          invalidateInvite()
          handleNavTo('/')
        }}
      />
    </TitledPaper>
  )
}

export default AcceptInvite
